import { SearchInput } from "@shared/components/SearchInput";
import { StepHelpers } from "@shared/components/Stepper";
import { toast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { FileText, LetterText, LucideUserSquare, Mail, MapPin, Phone, User, Users, CheckCircle2 } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useConsumerMetadata, useSearchConsumer } from "../../../hooks";
import { formatTerritoryAddress } from "@shared/utils/address";

interface CustomerSearchResult {
  id: string;
  label: string;
  description?: string;
  accountNumber: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  status?: string;
}

interface CustomerInformationStepProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  urlConsumerId?: number | null;
  urlConsumerNo?: string | null;
  source?: string | null;
}

export const CustomerInformationStep: React.FC<
  CustomerInformationStepProps
> = ({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
  urlConsumerId,
  urlConsumerNo,
  source,
}) => {
    const location = useLocation();
    const prefilledConsumer = location.state?.prefilledConsumer;
    const [searchParams] = useSearchParams();
    const paramConsumerId = searchParams.get('consumerId');
    const paramConsumerNo = searchParams.get('search');
    const stateConsumerId: number | null = paramConsumerId ? parseInt(paramConsumerId) : (urlConsumerId ?? prefilledConsumer?.consumerId ?? null);
    const stateConsumerNo: string | null = paramConsumerNo ?? urlConsumerNo ?? prefilledConsumer?.consumerNo ?? null;
    const prefilledName: string | null = prefilledConsumer?.name ?? null;

    const [searchTerm, setSearchTerm] = useState(prefilledName || stateConsumerNo || "");
    const [selectedConsumerId, setSelectedConsumerId] = useState<number | null>(
      null
    );
    const [isMetadataProcessed, setIsMetadataProcessed] = useState(false);
    const [isAccountLoaded, setIsAccountLoaded] = useState(false);
    const [customerData, setCustomerData] = useState({
      accountNumber: "",
      customerName: "",
      serviceAddress: "",
      contactPhone: "",
      contactEmail: "",
      status: ""
    });
    const dataLoadedRef = useRef(false);
    const urlParamsProcessedRef = useRef(false);
    const searchInputRef = useRef<any>(null);

    // Use the consumer search hook
    const { data: searchResults } = useSearchConsumer(
      searchTerm,
      remoteUtilityId
    );

    // Use the consumer metadata hook — only when we have a valid non-zero ID
    const { data: consumerMetadata } = useConsumerMetadata(
      selectedConsumerId && selectedConsumerId > 0 ? selectedConsumerId.toString() : null,
      remoteUtilityId
    );

    // Helper function to save step data using stepHelpers
    const saveStepData = useCallback(
      (data: any) => {
        if (!stepHelpers) return;

        try {
          const stepData = {
            ...data,
            timestamp: new Date().toISOString(),
            stepIndex: currentStepIndex,
          };

          stepHelpers.setStepData(currentStepIndex, stepData);
          console.log("Step data saved via stepHelpers:", stepData);
        } catch (error) {
          console.error("Failed to save step data via stepHelpers:", error);
          toast({
            title: "Failed to save progress",
            description: "Your progress may not be saved. Please try again.",
            variant: "destructive",
          });
        }
      },
      [stepHelpers, currentStepIndex]
    );

    // Helper function to get step data using stepHelpers
    const getStepData = useCallback(() => {
      if (!stepHelpers) return null;

      try {
        return stepHelpers.getStepData(currentStepIndex);
      } catch (error) {
        console.error("Failed to get step data via stepHelpers:", error);
        return null;
      }
    }, [stepHelpers, currentStepIndex]);

    // Process pre-filled consumer (from router state or URL params)
    useEffect(() => {
      if (
        stateConsumerId &&
        stateConsumerNo &&
        !urlParamsProcessedRef.current &&
        !dataLoadedRef.current
      ) {
        console.log("Processing pre-filled consumer:", { stateConsumerId, stateConsumerNo });

        // If we have a prefilled name, use it for the display, otherwise use consumer number
        const initialTerm = prefilledName || stateConsumerNo;
        setSearchTerm(initialTerm);

        // Set the selected consumer ID
        setSelectedConsumerId(stateConsumerId);

        // If we have prefilled consumer data, we can pre-populate some data immediately
        if (prefilledConsumer) {
          setCustomerData(prev => ({
            ...prev,
            accountNumber: prefilledConsumer.consumerNo || stateConsumerNo,
            customerName: prefilledName || "",
            contactPhone: prefilledConsumer.phone || "",
            contactEmail: prefilledConsumer.email || "",
            status: prefilledConsumer.status || ""
          }));
        }

        // Mark as processed
        urlParamsProcessedRef.current = true;

        console.log("Pre-filled consumer processed, search term set to:", initialTerm);
      }
    }, [stateConsumerId, stateConsumerNo, prefilledName, prefilledConsumer]);

    // Auto-select customer when search results are available and pre-fill is present
    useEffect(() => {
      if (
        stateConsumerId &&
        stateConsumerNo &&
        searchResults?.result &&
        urlParamsProcessedRef.current &&
        !dataLoadedRef.current
      ) {
        // Find the matching consumer in search results
        const matchingConsumer = searchResults.result.find(
          (consumer) => consumer.id === stateConsumerId && consumer.consumerNo === stateConsumerNo
        );

        if (matchingConsumer) {
          console.log("Auto-selecting pre-filled consumer:", matchingConsumer);

          const customerResult: CustomerSearchResult = {
            id: matchingConsumer.id.toString(),
            label: `${matchingConsumer.firstName} ${matchingConsumer.lastName}`,
            description: `${matchingConsumer.consumerNo}`,
            accountNumber: matchingConsumer.consumerNo,
            name: `${matchingConsumer.firstName} ${matchingConsumer.lastName}`,
            address: "",
            phone: "",
            email: "",
            status: matchingConsumer.statusDisplay,
          };

          // Auto-select the customer
          handleSelectCustomer(customerResult);

          // Mark as processed to prevent re-processing
          dataLoadedRef.current = true;
        }
      }
    }, [searchResults, stateConsumerId, stateConsumerNo]);

    // Load saved data from stepHelpers on component mount (only if no pre-filled consumer)
    useEffect(() => {
      if (stepHelpers && !dataLoadedRef.current && !stateConsumerId && !stateConsumerNo) {
        try {
          const savedStepData = getStepData();
          if (savedStepData) {
            // Populate customer data with saved data
            setCustomerData({
              accountNumber: savedStepData.accountNumber || "",
              customerName: savedStepData.customerName || "",
              serviceAddress: savedStepData.serviceAddress || "",
              contactPhone: savedStepData.contactPhone || "",
              contactEmail: savedStepData.contactEmail || "",
              status: savedStepData.status || "",
            });

            // Restore selected customer if available
            if (savedStepData.selectedConsumerId) {
              setSelectedConsumerId(savedStepData.selectedConsumerId);
              setIsAccountLoaded(true);
            }
          }
          dataLoadedRef.current = true;
        } catch (error) {
          console.error("Failed to load step data from stepHelpers:", error);
          dataLoadedRef.current = true;
        }
      }
    }, [stepHelpers, currentStepIndex, getStepData, urlConsumerId, urlConsumerNo]);

    // Update form with metadata when available
    useEffect(() => {
      if (consumerMetadata && selectedConsumerId && !isMetadataProcessed) {
        const metadata = consumerMetadata;

        // Build address string using configured format
        const fullAddress = formatTerritoryAddress(
          { service: metadata.serviceAddress },
          "service"
        )?.replace(/\n/g, ", ") || "";

        // Update customer data with complete information
        const formData = {
          customerName: `${metadata.firstName} ${metadata.lastName}`,
          serviceAddress: fullAddress,
          contactPhone: metadata.contactNumber,
          contactEmail: metadata.email,
          accountNumber: metadata.consumerNo,
          status: metadata.statusDisplay || "",
          utilityServices: metadata.utilityServices,
        };

        setCustomerData(formData);

        // Save complete data using stepHelpers
        saveStepData({
          ...formData,
          selectedConsumerId: selectedConsumerId,
          consumerMetadata: metadata,
        });

        // Mark metadata as processed to prevent re-running
        setIsMetadataProcessed(true);

        // Set account as loaded to enable the continue button
        setIsAccountLoaded(true);

        // Update search term to show customer name after loading
        if (stateConsumerId && stateConsumerNo) {
          setSearchTerm(`${metadata.firstName} ${metadata.lastName}`);
        }
      }
    }, [
      consumerMetadata,
      saveStepData,
      isMetadataProcessed,
      selectedConsumerId,
      stateConsumerId,
      stateConsumerNo,
    ]);

    // Reset metadata processed flag when a new customer is selected
    useEffect(() => {
      if (selectedConsumerId) {
        setIsMetadataProcessed(false);
      }
    }, [selectedConsumerId]);

    // Handle customer search
    const handleCustomerSearch = useCallback(
      async (term: string): Promise<CustomerSearchResult[]> => {
        if (term.length < 3) {
          return [];
        }

        setSearchTerm(term);

        // Map search results to the expected format
        const results =
          searchResults?.result?.map((consumer) => ({
            id: consumer.id.toString(),
            label: `${consumer.firstName} ${consumer.lastName}`,
            description: `#${consumer.consumerNo}`,
            accountNumber: consumer.consumerNo,
            name: `${consumer.firstName} ${consumer.lastName}`,
            address: "", // Will be populated when customer is selected
            phone: "", // Will be populated when customer is selected
            email: "", // Will be populated when customer is selected
            status: consumer.statusDisplay,
          })) || [];

        return results;
      },
      [searchResults]
    );

    // Handle customer selection
    const handleSelectCustomer = useCallback(
      (customer: CustomerSearchResult) => {
        const consumerId = parseInt(customer.id);
        setSelectedConsumerId(consumerId);

        // Update customer data with basic information
        setCustomerData(prev => ({
          ...prev,
          customerName: customer.name,
          accountNumber: customer.accountNumber,
          status: customer.status,
        }));

        // Save initial customer selection data using stepHelpers
        saveStepData({
          accountNumber: customer.accountNumber,
          customerName: customer.name,
          selectedConsumerId: consumerId,
          selectedCustomer: customer,
          isCustomerSelected: true,
        });

        // Ensure account is marked as loaded for URL-based selection
        setIsAccountLoaded(true);
      },
      [saveStepData]
    );

    // Render customer search result
    const renderCustomerResult = useCallback(
      (result: CustomerSearchResult) => (
        <div className="p-3 hover:bg-blue-50 cursor-pointer rounded border-l-4 border-transparent hover:border-blue-400 transition-all">
          <div className="font-medium text-gray-900">{result.name}</div>
          <div className="text-sm text-gray-600">
            Account #{result.accountNumber}
          </div>
          {result.status && (
            <div className="text-xs text-gray-500">{result.status}</div>
          )}
        </div>
      ),
      []
    );

    const handleContinue = () => {
      // Clear any previous validation errors
      if (clearValidationError) {
        clearValidationError();
      }

      // Save final data using stepHelpers
      saveStepData({
        ...customerData,
        selectedConsumerId,
        isStepComplete: true,
        completedAt: new Date().toISOString(),
      });

      // Log time for this step
      if (logTimeUntilStep) {
        logTimeUntilStep('Customer Information');
      }

      // Proceed to next step
      if (onNext) {
        onNext();
      }
    };

    const customerFields = useMemo(
      () => [
        {
          icon: LucideUserSquare,
          label: "Account:",
          value: customerData.accountNumber,
        },
        {
          icon: LetterText,
          label: "Name:",
          value: customerData.customerName,
        },
        {
          icon: Phone,
          label: "Phone:",
          value: customerData.contactPhone,
        },
        {
          icon: Mail,
          label: "Email:",
          value: customerData.contactEmail,
        },
        {
          icon: MapPin,
          label: "Address:",
          value: customerData.serviceAddress,
        },
        {
          icon: CheckCircle2,
          label: "Status:",
          value: customerData.status,
        },
      ],
      [customerData]
    );

    return (
      <div className="space-y-6">
        <div>
          <p className="text-gray-600">
            Search and select a customer to begin the complaint request.
          </p>
        </div>

        {/* Customer Search Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">
              Customer Search
            </h3>
          </div>

          <div className="relative">
            <SearchInput
              ref={searchInputRef}
              placeholder="Search by account number, name, phone or address..."
              onSearch={handleCustomerSearch}
              onSelect={handleSelectCustomer}
              renderResult={renderCustomerResult}
              minSearchLength={3}
              className="w-full"
              debounceMs={200}
              initialValue={searchTerm}
              disabled={source === 'account-management' || !!(searchParams.get('source') === 'account-management')}
            />
            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
              <FileText className="h-3 w-3" />
              Type at least 3 characters to search existing customers
            </p>
          </div>
        </div>

        {/* Selected Customer Information Display */}
        {selectedConsumerId && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 mb-3">
              <User className="h-4 w-4 text-green-600" />
              <h4 className="font-medium text-green-800">Selected Customer</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {customerFields.map((field, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-2`}
                >
                  <field.icon className="h-4 w-4 text-gray-500" />
                  <span className="font-medium text-gray-700">{field.label}</span>
                  <span className="text-gray-900">{field.value || "N/A"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <Button
            onClick={handleContinue}
            disabled={!isAccountLoaded || isValidating}
            className="min-w-[200px]"
          >
            {isValidating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Validating...
              </>
            ) : (
              "Continue to Complaint Details"
            )}
          </Button>
        </div>
      </div>
    );
  };
